import { createPortal } from 'react-dom';
import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCurrency, formatDate, CATEGORY_MAP, type TransactionTag } from '@tally/shared';
import { Sparkles, X, ExternalLink, MapPin, Phone, Hash, Calendar, Users, FileText, ChevronDown, ChevronUp, Trash2, Plane, Building, Moon, Mail } from 'lucide-react';
import { tagsApi, transactionsApi } from '@/services/api';
import { TagChip } from '@/components/tags/TagChip';
import { TagPicker } from '@/components/tags/TagPicker';
import { invalidateExpenseQueries, tripLabel, useTags, useTripOptions } from '@/components/tags/tagQueries';

interface TransactionDetail {
  id: string;
  date: string;
  merchantName: string;
  amount: number;
  currency?: string;
  category: string;
  tripId?: string | null;
  tripName?: string | null;
  tags?: TransactionTag[];
  /** Set when the expense is hidden as not spending: by itself, or by a rule for its merchant. */
  notSpending?: 'expense' | 'merchant' | null;
  description?: string | null;
  location?: { city?: string | null; country?: string | null } | null;
  sources?: Array<{ source: string; deepLink: string | null; accountName?: string }>;
  metadata?: Record<string, unknown>;
}

interface TransactionDetailPanelProps {
  transaction: TransactionDetail;
  onClose: () => void;
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
}

function hasMetadataDetails(meta: Record<string, unknown>): boolean {
  return !!(
    meta.hotelName ||
    meta.checkIn ||
    meta.checkOut ||
    meta.flightRoute ||
    meta.airline ||
    meta.confirmationNumber ||
    meta.guests ||
    meta.passengers ||
    meta.nights
  );
}

function formatStayRange(checkIn: string, checkOut: string, nights?: number): string {
  const inStr = formatDate(checkIn, 'short');
  const outStr = formatDate(checkOut, 'short');
  const nightsStr = nights ? ` (${nights} night${nights !== 1 ? 's' : ''})` : '';
  return `${inStr} → ${outStr}${nightsStr}`;
}

export function TransactionDetailPanel({ transaction: tx, onClose, onDelete, isDeleting }: TransactionDetailPanelProps) {
  const [showRaw, setShowRaw] = useState(false);
  const meta = (tx.metadata || {}) as Record<string, string | number | string[] | undefined>;
  const cat = CATEGORY_MAP.get(tx.category);

  // Portaled: inside a space-y container the sibling margin would push the fixed overlay down.
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md overflow-y-auto border-l border-border bg-card shadow-xl animate-in slide-in-from-right">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-6 py-4">
          <h2 className="text-lg font-semibold">Transaction Details</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-accent">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 p-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: (cat?.color || '#94a3b8') + '20' }}>
                <span className="text-lg">{getEmoji(tx.category)}</span>
              </div>
              <div>
                <h3 className="text-lg font-bold">{tx.merchantName}</h3>
                <p className="text-sm text-muted-foreground">{cat?.label || tx.category}</p>
              </div>
            </div>
            <p className="mt-3 text-3xl font-bold">{formatCurrency(tx.amount, tx.currency)}</p>
            <p className="text-sm text-muted-foreground">{tx.date}</p>
          </div>

          <OrganizeSection key={tx.id} transaction={tx} />
          <NotSpendingSection key={`ns-${tx.id}`} transaction={tx} onDone={onClose} />

          {tx.location && (tx.location.city || tx.location.country) && (
            <DetailRow icon={MapPin} label="Location" value={[tx.location.city, tx.location.country].filter(Boolean).join(', ')} />
          )}

          {/* Parsed Metadata Card */}
          {hasMetadataDetails(meta) && (
            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Booking Details</p>

              {meta.hotelName && (
                <DetailRow icon={Building} label="Hotel" value={String(meta.hotelName)} />
              )}

              {meta.checkIn && meta.checkOut && (
                <DetailRow
                  icon={Moon}
                  label="Stay"
                  value={formatStayRange(
                    String(meta.checkIn),
                    String(meta.checkOut),
                    typeof meta.nights === 'number' ? meta.nights : undefined
                  )}
                />
              )}

              {meta.checkIn && !meta.checkOut && (
                <DetailRow icon={Calendar} label="Check-in" value={formatDate(String(meta.checkIn), 'short')} />
              )}

              {meta.flightRoute && (
                <DetailRow icon={Plane} label="Route" value={String(meta.flightRoute)} />
              )}

              {meta.airline && (
                <DetailRow icon={Plane} label="Airline" value={String(meta.airline)} />
              )}

              {meta.confirmationNumber && (
                <DetailRow icon={Hash} label="Confirmation" value={String(meta.confirmationNumber)} copyable />
              )}

              {(meta.guests || meta.passengers) && (
                <DetailRow
                  icon={Users}
                  label={meta.guests ? 'Guests' : 'Passengers'}
                  value={((meta.guests || meta.passengers) as string[]).join(', ')}
                />
              )}
            </div>
          )}

          {/* Legacy fields that are not in the new card */}
          {!hasMetadataDetails(meta) && meta.confirmationNumber && (
            <DetailRow icon={Hash} label="Confirmation #" value={String(meta.confirmationNumber)} copyable />
          )}

          {meta.address && (
            <DetailRow icon={MapPin} label="Address" value={String(meta.address)} />
          )}

          {meta.phone && (
            <DetailRow icon={Phone} label="Contact" value={String(meta.phone)} />
          )}

          {meta.dateRange && !meta.checkIn && (
            <DetailRow icon={Calendar} label="Dates" value={`${(meta.dateRange as any).start} → ${(meta.dateRange as any).end}`} />
          )}

          {!hasMetadataDetails(meta) && meta.passengers && (
            <DetailRow icon={Users} label="Passengers" value={(meta.passengers as string[]).join(', ')} />
          )}

          {!!meta.flightDetails && (
            <DetailRow icon={FileText} label="Flight" value={String(meta.flightDetails)} />
          )}

          {meta.items && Array.isArray(meta.items) && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Items</p>
              <div className="rounded-lg border border-border p-3 space-y-1">
                {(meta.items as string[]).map((item, i) => (
                  <p key={i} className="text-sm">{item}</p>
                ))}
              </div>
            </div>
          )}

          {meta.notes && (
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Notes</p>
              <p className="text-sm rounded-lg border border-border p-3 whitespace-pre-wrap">{String(meta.notes)}</p>
            </div>
          )}

          {tx.sources && tx.sources.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Sources</p>
              <div className="space-y-2">
                {tx.sources.map((s, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="text-sm font-medium">{s.accountName || s.source}</span>
                    {s.deepLink && (
                      <a href={s.deepLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <EmailContentSection transactionId={tx.id} />

          {Object.keys(meta).length > 0 && (
            <div>
              <button
                onClick={() => setShowRaw(!showRaw)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                {showRaw ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {showRaw ? 'Hide' : 'Show'} raw metadata
              </button>
              {showRaw && (
                <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-muted p-3 text-xs font-mono">
                  {JSON.stringify(meta, null, 2)}
                </pre>
              )}
            </div>
          )}

          {onDelete && (
            <div className="border-t border-border pt-4">
              <DeleteConfirmButton
                onConfirm={() => onDelete(tx.id)}
                isDeleting={isDeleting}
              />
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Card bill payments, transfers between one's own accounts and the like are not spending: hiding them stops
 * them being counted on top of the purchases they paid for. Hiding closes the panel (the expense leaves the list).
 */
function NotSpendingSection({ transaction: tx, onDone }: { transaction: TransactionDetail; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const change = useMutation({
    mutationFn: (body: { notSpending: boolean; wholeMerchant?: boolean }) => transactionsApi.setNotSpending(tx.id, body),
    onSuccess: () => {
      invalidateExpenseQueries(queryClient);
      for (const key of ['insights', 'subscriptions']) queryClient.invalidateQueries({ queryKey: [key] });
      onDone();
    },
  });
  const error = change.error instanceof Error ? change.error.message : change.error ? 'Could not save the change' : null;

  if (tx.notSpending) {
    return (
      <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/5 p-4">
        <p className="text-sm">
          Not counted as spending{tx.notSpending === 'merchant' ? <> — like every expense from <span className="font-medium">{tx.merchantName}</span></> : ''}.
        </p>
        <button
          onClick={() => change.mutate({ notSpending: false })}
          disabled={change.isPending}
          className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          {tx.notSpending === 'merchant' ? `Count ${tx.merchantName} as spending again` : 'Count as spending again'}
        </button>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!asking ? (
        <button onClick={() => setAsking(true)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
          Not spending? (a card bill payment, a transfer between your accounts…)
        </button>
      ) : (
        <div className="space-y-2 rounded-lg border border-border p-4">
          <p className="text-sm">Hide this from your spending totals. Purchases paid with a card are already counted on their own, so the card bill would count them twice.</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => change.mutate({ notSpending: true })}
              disabled={change.isPending}
              className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-accent disabled:opacity-50"
            >
              Just this one
            </button>
            <button
              onClick={() => change.mutate({ notSpending: true, wholeMerchant: true })}
              disabled={change.isPending}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              Everything from {tx.merchantName}
            </button>
            <button onClick={() => setAsking(false)} className="px-2 text-sm text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
}

/** Which trip the expense is on and which tags it carries; changes save right away. */
function OrganizeSection({ transaction: tx }: { transaction: TransactionDetail }) {
  const queryClient = useQueryClient();
  const allTags = useTags();
  const trips = useTripOptions();
  // Local copies so the panel reflects edits before the list it was opened from reloads (keyed by id).
  const [tripId, setTripId] = useState<string | null>(tx.tripId ?? null);
  const [tags, setTags] = useState<TransactionTag[]>(tx.tags ?? []);

  const move = useMutation({
    mutationFn: (next: string | null) => transactionsApi.update(tx.id, { tripId: next }),
    onSuccess: (_res, next) => { setTripId(next); invalidateExpenseQueries(queryClient); },
  });
  const edit = useMutation({
    mutationFn: (change: { add?: { id: string; name: string }; remove?: string }) =>
      transactionsApi.bulk({
        transactionIds: [tx.id],
        addTagIds: change.add ? [change.add.id] : undefined,
        removeTagIds: change.remove ? [change.remove] : undefined,
      }),
    onSuccess: (_res, change) => {
      if (change.add) {
        const known = allTags.find((t) => t.id === change.add!.id);
        const added: TransactionTag = { id: change.add.id, name: change.add.name, kind: known?.kind ?? 'topic', color: known?.color ?? null, source: 'user' };
        setTags((prev) => [...prev.filter((t) => t.id !== added.id), added].sort((a, b) => a.name.localeCompare(b.name)));
      }
      if (change.remove) setTags((prev) => prev.filter((t) => t.id !== change.remove));
      invalidateExpenseQueries(queryClient);
    },
  });
  const { data: suggestionData } = useQuery({ queryKey: ['tag-suggestions', tx.id, tags.length], queryFn: () => tagsApi.suggestions([tx.id]) });
  const suggestions = (suggestionData?.data ?? []).filter((sg) => !tags.some((t) => t.name.toLowerCase() === sg.name.toLowerCase()));
  const addSuggestion = async (sg: { name: string; tagId: string | null }) => {
    const id = sg.tagId ?? (await tagsApi.create({ name: sg.name })).data.id;
    edit.mutate({ add: { id, name: sg.name } });
  };
  const known = trips.some((t) => t.id === tripId);
  const error = move.error ?? edit.error;

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div>
        <label htmlFor={`trip-${tx.id}`} className="mb-1.5 block text-xs font-medium text-muted-foreground">Trip</label>
        <select
          id={`trip-${tx.id}`}
          value={tripId ?? ''}
          disabled={move.isPending}
          onChange={(e) => move.mutate(e.target.value || null)}
          className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
        >
          <option value="">No trip</option>
          {tripId && !known && <option value={tripId}>{tx.tripName || 'Current trip'}</option>}
          {trips.map((t) => <option key={t.id} value={t.id}>{tripLabel(t)}</option>)}
        </select>
      </div>
      <div>
        <p className="mb-1.5 text-xs font-medium text-muted-foreground">Tags</p>
        {tags.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <TagChip key={t.id} name={t.name} color={t.color} source={t.source} disabled={edit.isPending} onRemove={() => edit.mutate({ remove: t.id })} />
            ))}
          </div>
        )}
        {suggestions.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {suggestions.map((sg) => (
              <button
                key={sg.name}
                type="button"
                disabled={edit.isPending}
                onClick={() => void addSuggestion(sg)}
                title={`Suggested by AI (${Math.round(sg.confidence * 100)}% sure) — click to add`}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary/50 px-2 py-0.5 text-xs text-primary hover:bg-primary/10 disabled:opacity-50"
              >
                <Sparkles className="h-3 w-3" /> {sg.name}
              </button>
            ))}
          </div>
        )}
        <TagPicker tags={allTags} excludeIds={tags.map((t) => t.id)} suggestions={suggestionData?.data} disabled={edit.isPending} onPick={(tag) => edit.mutate({ add: tag })} />
      </div>
      {error && <p className="text-xs text-destructive">{error instanceof Error ? error.message : 'Could not save the change'}</p>}
    </div>
  );
}

function EmailContentSection({ transactionId }: { transactionId: string }) {
  const [emails, setEmails] = useState<Array<{ messageId: string; subject: string; from: string; date: string; body: string }>>([]);
  const [loaded, setLoaded] = useState(false);
  const [showSection, setShowSection] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (showSection && !loaded) {
      transactionsApi.emails(transactionId)
        .then((res) => setEmails(res.data))
        .catch(() => setEmails([]))
        .finally(() => setLoaded(true));
    }
  }, [transactionId, showSection, loaded]);

  return (
    <div>
      <button
        onClick={() => setShowSection(!showSection)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
      >
        <Mail className="h-3 w-3" />
        {showSection ? 'Hide source emails' : 'Show source emails'}
        {showSection ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {showSection && loaded && emails.length > 0 && (
        <div className="mt-2 space-y-2">
          {emails.map((email) => (
            <div key={email.messageId} className="rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => setExpanded(expanded === email.messageId ? null : email.messageId)}
                className="flex w-full items-center gap-2 p-2.5 text-left hover:bg-accent/30"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{email.subject}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{email.from}</p>
                </div>
                {expanded === email.messageId ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
              </button>
              {expanded === email.messageId && (
                <div className="border-t border-border bg-muted/20 p-3">
                  <pre className="max-h-72 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground font-mono leading-relaxed">
                    {email.body || 'No content available'}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {showSection && loaded && emails.length === 0 && (
        <p className="mt-1 text-xs text-muted-foreground">No cached email content available</p>
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, copyable }: { icon: any; label: string; value: string; copyable?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-words">{value}</p>
      </div>
      {copyable && (
        <button onClick={handleCopy} className="shrink-0 rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent">
          {copied ? 'Copied!' : 'Copy'}
        </button>
      )}
    </div>
  );
}

function DeleteConfirmButton({ onConfirm, isDeleting }: { onConfirm: () => void; isDeleting?: boolean }) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-destructive">Delete this transaction?</span>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="rounded-lg bg-destructive px-3 py-1.5 text-xs font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
        >
          {isDeleting ? 'Deleting...' : 'Confirm'}
        </button>
        <button
          onClick={() => setConfirming(false)}
          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="flex items-center gap-2 rounded-lg border border-destructive/30 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10"
    >
      <Trash2 className="h-4 w-4" /> Delete Transaction
    </button>
  );
}

function getEmoji(category: string): string {
  const map: Record<string, string> = {
    accommodation: '🏨', flights: '✈️', ground_transport: '🚗', car_rental: '🔑',
    food_dining: '🍽️', groceries: '🛒', shopping: '🛍️', entertainment: '🎭',
    activities: '🧭', health_medical: '💊', communication: '📱', insurance: '🛡️',
    fees_charges: '🧾', tips: '💰', gifts_souvenirs: '🎁', laundry: '👔',
    parking_tolls: '🅿️', fuel: '⛽', subscriptions: '🔄', utilities: '⚡', other: '📌',
  };
  return map[category] || '📌';
}
